import type { MedicationPadAlertError } from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import type { MedicationType } from '@/components/Patient/components/ProductDetailsCard/ProductDetailsCard.types';
import { sanitizeLatin1SafeInput } from '@/components/Patient/utils/latin1Safe/sanitizeLatin1SafeInput';
import { validateLatin1SafeInput } from '@/components/Patient/utils/latin1Safe/validateLatin1SafeInput';
import { appendDosageSuffix, removeDosageSuffix } from '@/utils/dosageLimit/dosageSuffix';
import { ALERT_ACTION_REQUIRED_TITLE } from '../constants';
import {
  DOSAGE_EMPTY_ERROR_MESSAGE,
  DOSAGE_MAX_LENGTH,
  DOSAGE_MAX_LENGTH_ALERT_MESSAGE,
  DOSAGE_MAX_LENGTH_ERROR_MESSAGE,
  validateDosageLength,
} from './validateDosageLength';

export type DosageInlineErrors = {
  inlineErrors: string[];
};

/** A dosage length problem: too little text (including none) or too much */
type DosageLengthError = { kind: 'min' | 'max'; message: string };

export type DosageValidationResult = DosageInlineErrors & {
  alertError: MedicationPadAlertError;
};

/**
 * Returns the value that should be used for character count and length validation.
 * When sanitisation is enabled, we validate against the sanitised preview so
 * that the value that will be submitted is validated.
 * (e.g. control chars removed, "…" becomes "...").
 */
export const getDosageValueForLengthValidation = (rawDosage: string) => sanitizeLatin1SafeInput(rawDosage).value;

/**
 * @param suffix - the dispense limit phrases appended to the dosage at the review step; the maximum applies to the
 * dosage with them appended, since that is the dosage prescribed
 */
export const getDosageLengthError = (valueToCheck: string, suffix?: string): DosageLengthError | undefined => {
  const prescribedDosage = suffix ? appendDosageSuffix(valueToCheck, suffix) : valueToCheck;

  // The minimum counts the typed text alone: the suffix is written for the pharmacy, and the instructions are for the
  // patient, so they must be detailed on their own. At the review step the suffix is already written into the text
  const typedDosage = suffix ? removeDosageSuffix(valueToCheck, suffix) ?? '' : valueToCheck;
  const { hasError, errorMessage } = validateDosageLength(typedDosage);
  if (hasError && errorMessage) {
    return { kind: 'min', message: errorMessage };
  }

  // The maximum applies only once a suffix is written. The textarea caps typing, so text over it can only have come
  // stored on the product: prescribable without a suffix, but with dispense limits on every Herb, Dried item with a
  // dispense limit row carries the monthly phrase, so a long stored product text is flagged and blocks review
  const isOverMaxLength = Boolean(suffix) && prescribedDosage.length > DOSAGE_MAX_LENGTH;
  return isOverMaxLength ? { kind: 'max', message: DOSAGE_MAX_LENGTH_ERROR_MESSAGE } : undefined;
};

/**
 * @param suffix - see `getDosageLengthError`
 */
export const validateDosage = (dosage: string, productId?: number, suffix?: string): DosageValidationResult => {
  // No product selected: do not validate or sanitise
  // This prevents us from showing errors when changing medication filters
  if (productId === undefined) {
    return {
      inlineErrors: [],
      alertError: null,
    };
  }

  const valueToCheck = getDosageValueForLengthValidation(dosage);
  const inlineErrors: string[] = [];

  // Validate characters in the sanitised preview.
  const { hasError, errorMessage } = validateLatin1SafeInput(valueToCheck);
  if (hasError && errorMessage) {
    inlineErrors.push(errorMessage);
  }

  const lengthError = getDosageLengthError(valueToCheck, suffix);
  if (lengthError) {
    inlineErrors.push(lengthError.message);
  }
  // The alert words a too-long dosage apart from its inline error
  const isFirstErrorTooLong = lengthError?.kind === 'max' && inlineErrors.length === 1;

  const alertError =
    inlineErrors.length === 0
      ? null
      : {
          title: inlineErrors[0] === DOSAGE_EMPTY_ERROR_MESSAGE ? 'Dosage Required:' : ALERT_ACTION_REQUIRED_TITLE,
          description: isFirstErrorTooLong ? DOSAGE_MAX_LENGTH_ALERT_MESSAGE : inlineErrors[0],
        };

  return {
    inlineErrors,
    alertError,
  };
};

// Convenience wrapper to get just the alert error
export const getDosageAlertError = (
  medicationPadItemDetail?: MedicationType,
  suffix?: string,
): MedicationPadAlertError => {
  const productId = medicationPadItemDetail?.productId;

  if (productId === undefined) {
    return null;
  }

  return validateDosage(medicationPadItemDetail?.dosage ?? '', productId, suffix).alertError;
};

/**
 * @param reservedLength - characters added to the dosage after editing (e.g. the dispense limit phrases),
 * counted so the total reflects the dosage as prescribed
 */
export const getDosageCharacterCountText = (dosage: string, maxLength: number, reservedLength = 0) => {
  // Trimmed again: removing a character can leave whitespace at the end, which the written dosage drops
  const valueForCount = getDosageValueForLengthValidation(dosage).trim();
  return `${valueForCount.length + reservedLength}/${maxLength}`;
};
